import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import * as THREE from "three";
import ConsumerSiteBanner from "@/components/shared/ConsumerSiteBanner";

export default function Landing() {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLDivElement>(null);
  const [showLogin, setShowLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Typewriter effect
  const [logoText, setLogoText] = useState("");
  const [taglineText, setTaglineText] = useState("");
  const fullLogo = "TCGL VENTURES";
  const fullTagline =
    "Improving the way we live, work & play for future generations";

  useEffect(() => {
    let logoIndex = 0;
    let taglineIndex = 0;

    const typeWriter = () => {
      if (logoIndex < fullLogo.length) {
        setLogoText(fullLogo.substring(0, logoIndex + 1));
        logoIndex++;
        setTimeout(typeWriter, 100);
      } else if (taglineIndex < fullTagline.length) {
        setTaglineText(fullTagline.substring(0, taglineIndex + 1));
        taglineIndex++;
        setTimeout(typeWriter, 30);
      }
    };

    setTimeout(typeWriter, 500);
  }, []);

  // Three.js particle background
  useEffect(() => {
    if (!canvasRef.current) return;

    const container = canvasRef.current;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });

    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    camera.position.z = 50;

    const isMobile = window.innerWidth < 768;
    const particleCount = isMobile ? 800 : 1500;
    const particles = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);
    const velocities: { x: number; y: number; z: number }[] = [];

    for (let i = 0; i < particleCount * 3; i += 3) {
      const radius = Math.random() * 40 + 10;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;

      positions[i] = radius * Math.sin(phi) * Math.cos(theta);
      positions[i + 1] = radius * Math.sin(phi) * Math.sin(theta);
      positions[i + 2] = radius * Math.cos(phi);

      velocities.push({
        x: (Math.random() - 0.5) * 0.02,
        y: (Math.random() - 0.5) * 0.02,
        z: (Math.random() - 0.5) * 0.02,
      });
    }

    particles.setAttribute("position", new THREE.BufferAttribute(positions, 3));

    const particleMaterial = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 0.2,
      transparent: true,
      opacity: 0.4,
      blending: THREE.AdditiveBlending,
    });

    const particleSystem = new THREE.Points(particles, particleMaterial);
    scene.add(particleSystem);

    const lineGeometry = new THREE.BufferGeometry();
    const lineMaterial = new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.05,
      blending: THREE.AdditiveBlending,
    });

    const maxDistance = 8;
    const maxConnections = isMobile ? 10 : 20;

    function updateLines() {
      const linePositions: number[] = [];
      const positionArray = particles.attributes.position.array as Float32Array;

      for (let i = 0; i < particleCount; i++) {
        const x1 = positionArray[i * 3];
        const y1 = positionArray[i * 3 + 1];
        const z1 = positionArray[i * 3 + 2];

        for (
          let j = i + 1;
          j < Math.min(i + maxConnections, particleCount);
          j++
        ) {
          const x2 = positionArray[j * 3];
          const y2 = positionArray[j * 3 + 1];
          const z2 = positionArray[j * 3 + 2];

          const distance = Math.sqrt(
            Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2) + Math.pow(z2 - z1, 2)
          );

          if (distance < maxDistance) {
            linePositions.push(x1, y1, z1, x2, y2, z2);
          }
        }
      }

      lineGeometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(linePositions, 3)
      );
    }

    const lineSystem = new THREE.LineSegments(lineGeometry, lineMaterial);
    scene.add(lineSystem);

    let time = 0;
    function animate() {
      requestAnimationFrame(animate);
      time += 0.001;

      const positionArray = particles.attributes.position.array as Float32Array;

      for (let i = 0; i < particleCount; i++) {
        const i3 = i * 3;

        positionArray[i3] += velocities[i].x;
        positionArray[i3 + 1] += velocities[i].y;
        positionArray[i3 + 2] += velocities[i].z;

        positionArray[i3 + 1] += Math.sin(time + positionArray[i3]) * 0.01;

        const axes = ["x", "y", "z"];
        axes.forEach((axis, idx) => {
          const coord = i3 + idx;
          if (Math.abs(positionArray[coord]) > 60) {
            positionArray[coord] *= -0.9;
            velocities[i][axis as keyof (typeof velocities)[0]] *= -1;
          }
        });
      }

      particles.attributes.position.needsUpdate = true;

      if (Math.floor(time * 100) % 5 === 0) {
        updateLines();
      }

      particleSystem.rotation.y += 0.0003;
      lineSystem.rotation.y += 0.0003;

      renderer.render(scene, camera);
    }

    animate();

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      container.removeChild(renderer.domElement);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const dropdown = document.getElementById("loginDropdown");
      const signInBtn = document.querySelector(".btn-primary");

      if (
        dropdown &&
        !dropdown.contains(e.target as Node) &&
        e.target !== signInBtn
      ) {
        setShowLogin(false);
        setError("");
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        // Navigate to loading/splash screen
        navigate("/loading");
      }
    } catch (err: any) {
      setError(err.message || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAccess = () => {
    window.location.href =
      "mailto:hello@tcglventures.com?subject=Portal Access Request&body=I would like to request access to the TCGL Ventures investment portal.";
  };

  return (
    <div className="relative h-screen overflow-hidden bg-gradient-to-br from-black via-black to-gray-900">
      {/* Three.js Canvas */}
      <div ref={canvasRef} className="fixed inset-0 z-0" />

      {/* Header Buttons */}
      <div className="fixed top-8 right-8 z-30 flex gap-4">
        <button
          onClick={() => setShowLogin(!showLogin)}
          className="btn-primary px-5 py-2.5 text-sm font-bold uppercase tracking-wider border-2 border-white bg-white text-black transition-all hover:bg-transparent hover:text-white"
          style={{ fontFamily: "'Courier New', monospace" }}
        >
          Sign In
        </button>
        <button
          onClick={handleRequestAccess}
          className="px-5 py-2.5 text-sm font-bold uppercase tracking-wider border-2 border-white bg-transparent text-white transition-all hover:bg-white hover:text-black"
          style={{ fontFamily: "'Courier New', monospace" }}
        >
          Request Access
        </button>
      </div>

      {/* Login Dropdown */}
      {showLogin && (
        <div
          id="loginDropdown"
          className="fixed top-20 right-8 z-40 w-72 bg-white/98 border-2 border-white p-5 shadow-lg"
        >
          {error && (
            <div
              className="mb-3 bg-red-600 text-white text-xs text-center p-2 border border-black"
              style={{ fontFamily: "'Courier New', monospace" }}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleLogin}>
            <div className="mb-3">
              <label
                className="block mb-1 text-black font-bold text-xs uppercase tracking-wide"
                style={{ fontFamily: "'Courier New', monospace" }}
              >
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="email@example.com"
                className="w-full px-3 py-2 border-2 border-black bg-white text-sm text-black min-h-[40px] focus:outline-none focus:border-gray-600"
                style={{ fontFamily: "'Courier New', monospace" }}
              />
            </div>

            <div className="mb-3">
              <label
                className="block mb-1 text-black font-bold text-xs uppercase tracking-wide"
                style={{ fontFamily: "'Courier New', monospace" }}
              >
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="password"
                className="w-full px-3 py-2 border-2 border-black bg-white text-sm text-black min-h-[40px] focus:outline-none focus:border-gray-600"
                style={{ fontFamily: "'Courier New', monospace" }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full mt-2 px-3 py-2 border-2 border-black bg-black text-white text-xs font-bold uppercase tracking-wider transition-all hover:bg-white hover:text-black min-h-[44px] disabled:opacity-50"
              style={{ fontFamily: "'Courier New', monospace" }}
            >
              {loading ? "Signing In..." : "Sign In"}
            </button>
          </form>
        </div>
      )}

      {/* Center Content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-5">
        <h1
          className="text-4xl md:text-5xl font-black tracking-[0.2em] text-white"
          style={{ fontFamily: "'Courier New', monospace" }}
        >
          {logoText}
          {logoText === fullLogo && <span className="animate-pulse">|</span>}
        </h1>
      </div>

      {/* Bottom Tagline */}
      <div className="fixed bottom-10 left-0 right-0 z-10 flex justify-center px-5">
        <p
          className="text-sm md:text-base italic text-gray-500 max-w-2xl text-center leading-relaxed"
          style={{ fontFamily: "'Courier New', monospace" }}
        >
          {taglineText}
          {taglineText === fullTagline && (
            <span className="animate-pulse">|</span>
          )}
        </p>
      </div>
      {/* Consumer Site Banner */}
      <ConsumerSiteBanner />
    </div>
  );
}
